import { useRef, useState } from 'react'
import { Maximize2, Minimize2 } from 'lucide-react'
import { HelpTopic } from '@/content/help'
import { FILM_FORMATS, FilmFormatId } from '@/content/instantFilm'
import { EditAction } from '@/core/history'
import { useEditSession } from '@/core/store/editSession'
import { MorePanelId } from '@/core/store/uiState'
import { selectParentFrame } from '@/core/store/selectors'
import { TEST_IDS } from '@/lib/testIds'
import { updateSelectedPlainFrameParams } from '@/tools/collage/actions'
import {
  addPolaroidFrame,
  recenterSelectedFreePlacement,
  removeFrame,
  setSelectedFrameFormat,
  setSelectedFramePaperColor,
  setSelectedFreePlacementScale,
  setSelectedPhotoPlacement,
  updateSelectedFrameParams,
} from '@/tools/frame/actions'
import {
  CAPTION_MAX_LENGTH,
  DateFormatId,
  FrameStyleId,
  FREE_PLACEMENT_MAX_SCALE,
  FREE_PLACEMENT_MIN_SCALE,
  MAX_PLAIN_FRAME_BORDER_MM,
  MIN_PLAIN_FRAME_BORDER_MM,
  PAPER_COLOR_PRESETS,
  PaperPresetId,
  PhotoPlacement,
  toIsoDate,
} from '@/tools/frame/params'
import { ChoiceGroup, ChoiceTile } from '@/ui/ChoiceTile'
import { IconSlider } from '@/ui/IconSlider'
import { InfoTip } from '@/ui/InfoTip'
import {
  ColorWheelIllustration,
  DateIllustration,
  FilmFormatIllustration,
  PlacementIllustration,
  PolaroidIllustration,
  ShadowIllustration,
} from '@/ui/illustrations/PanelIllustrations'
import { MoreSection } from '@/ui/MoreSection'
import { Button } from '@/ui/shadcn/button'

const PAPER_PRESET_LABELS: Readonly<Record<PaperPresetId, string>> = {
  [PaperPresetId.White]: 'White',
  [PaperPresetId.Warm]: 'Warm',
  [PaperPresetId.Cream]: 'Cream',
  [PaperPresetId.Black]: 'Black',
  [PaperPresetId.Sand]: 'Sand',
  [PaperPresetId.Sage]: 'Sage',
}

const FILM_FORMAT_LABELS: Readonly<Record<FilmFormatId, string>> = {
  [FilmFormatId.PolaroidClassic]: 'Classic',
  [FilmFormatId.PolaroidGo]: 'Go',
  [FilmFormatId.InstaxMini]: 'Mini',
}

const DATE_FORMAT_LABELS: Readonly<Record<DateFormatId, string>> = {
  [DateFormatId.DayMonthYear]: '31.12.2026',
  [DateFormatId.MonthDayYear]: '12/31/2026',
  [DateFormatId.LongMonth]: 'Dec 31, 2026',
  [DateFormatId.FilmStamp]: "'26 12 31",
}

function PlainFramePanel() {
  const selected = useEditSession(selectParentFrame)
  const commitHistory = useEditSession((state) => state.commitHistory)
  if (!selected || selected.params.styleId !== FrameStyleId.Plain) {
    throw new Error('Plain frame panel requires a selected Plain frame')
  }
  const { params } = selected
  return (
    <section className="space-y-5" aria-labelledby="plain-frame-heading">
      <h2 id="plain-frame-heading" className="font-semibold">Plain frame</h2>
      <div className="flex items-center gap-3">
        <label className="relative size-10 cursor-pointer overflow-hidden rounded-full border shadow-sm" style={{ backgroundColor: params.color }}>
          <span className="sr-only">Plain frame color</span>
          <input
            type="color"
            value={params.color}
            data-testid={TEST_IDS.plainFrameColor}
            onChange={(event) => updateSelectedPlainFrameParams({ color: event.currentTarget.value })}
            onBlur={() => commitHistory(EditAction.EditFrame)}
            className="absolute inset-0 size-full cursor-pointer opacity-0"
          />
        </label>
        <IconSlider
          className="flex-1"
          min={MIN_PLAIN_FRAME_BORDER_MM}
          max={MAX_PLAIN_FRAME_BORDER_MM}
          step={0.5}
          value={params.borderMm}
          startIcon={<Minimize2 className="size-4" />}
          endIcon={<Maximize2 className="size-4" />}
          formatValue={(value) => `${value} mm`}
          data-testid={TEST_IDS.plainFrameBorder}
          aria-label="Plain frame border width"
          onValueChange={(borderMm) => updateSelectedPlainFrameParams({ borderMm })}
          onValueCommit={(borderMm) => updateSelectedPlainFrameParams({ borderMm }, true)}
        />
      </div>
      <ChoiceTile
        ariaLabel="Change to Polaroid frame"
        label="Polaroid"
        illustration={<PolaroidIllustration />}
        data-testid={TEST_IDS.frameAdd}
        onClick={addPolaroidFrame}
        className="w-full"
      />
      <Button type="button" variant="ghost" data-testid={TEST_IDS.frameRemove} onClick={removeFrame}>Remove frame</Button>
    </section>
  )
}

export function FramePanel() {
  const selected = useEditSession(selectParentFrame)
  const commitHistory = useEditSession((state) => state.commitHistory)
  const customColorInput = useRef<HTMLInputElement>(null)
  const captionInput = useRef<HTMLInputElement>(null)
  const [captionOpen, setCaptionOpen] = useState(false)

  if (!selected) {
    return (
      <section className="space-y-4" aria-labelledby="frame-heading">
        <h2 id="frame-heading" className="font-semibold">Frame</h2>
        <ChoiceTile
          ariaLabel="Add Polaroid frame"
          label="Polaroid"
          illustration={<PolaroidIllustration />}
          data-testid={TEST_IDS.frameAdd}
          onClick={addPolaroidFrame}
          className="w-full py-4"
        />
      </section>
    )
  }
  if (selected.params.styleId === FrameStyleId.Plain) return <PlainFramePanel />

  const { params } = selected
  const freePlacement = params.free
  if (params.placement === PhotoPlacement.Free && !freePlacement) {
    throw new Error('Free photo placement is missing its required scale and offsets')
  }
  const dateOpen = params.showWrittenDate || params.showStampDate
  const showCaptionInput = captionOpen || params.caption.length > 0

  return (
    <section className="space-y-5" aria-labelledby="polaroid-heading">
      <h2 id="polaroid-heading" className="font-semibold">Polaroid</h2>

      <div className="space-y-2">
        <div className="flex items-center gap-1"><h3 className="text-sm font-medium">Format</h3><InfoTip topic={HelpTopic.FilmFormats} /></div>
        <ChoiceGroup mode="single" label="Film format" className="grid-cols-3">
          {FILM_FORMATS.map((format) => (
            <ChoiceTile
              key={format.id}
              ariaLabel={FILM_FORMAT_LABELS[format.id]}
              label={FILM_FORMAT_LABELS[format.id]}
              illustration={<FilmFormatIllustration format={format} />}
              selected={params.formatId === format.id}
              data-testid={TEST_IDS.frameFormat(format.id)}
              onClick={() => setSelectedFrameFormat(format.id)}
            />
          ))}
        </ChoiceGroup>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Paper</h3>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Paper color">
          {PAPER_COLOR_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              aria-label={`${PAPER_PRESET_LABELS[preset.id]} paper`}
              aria-pressed={params.paperColor === preset.color}
              data-testid={TEST_IDS.framePaperPreset(preset.id)}
              onClick={() => setSelectedFramePaperColor(preset.color)}
              className="size-10 rounded-xl border shadow-sm outline-offset-2 aria-pressed:ring-2 aria-pressed:ring-primary"
              style={{ backgroundColor: preset.color }}
            />
          ))}
          <button
            type="button"
            aria-label="Custom paper color"
            className="size-10 rounded-xl border p-1.5 outline-offset-2"
            onClick={() => {
              const input = customColorInput.current
              if (!input) throw new Error('Custom paper color input is not configured')
              input.click()
            }}
          >
            <ColorWheelIllustration />
          </button>
          <input
            ref={customColorInput}
            type="color"
            value={params.paperColor}
            data-testid={TEST_IDS.framePaperColor}
            onChange={(event) => setSelectedFramePaperColor(event.currentTarget.value, false)}
            onBlur={() => commitHistory(EditAction.EditFrame)}
            className="sr-only"
            aria-label="Choose custom paper color"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-1"><h3 className="text-sm font-medium">Placement</h3><InfoTip topic={HelpTopic.FramePlacement} /></div>
        <ChoiceGroup mode="single" label="Photo placement" className="grid-cols-2">
          {Object.values(PhotoPlacement).map((placement) => (
            <ChoiceTile
              key={placement}
              ariaLabel={placement === PhotoPlacement.Fill ? 'Fill' : 'Float'}
              label={placement === PhotoPlacement.Fill ? 'Fill' : 'Float'}
              illustration={<PlacementIllustration placement={placement} />}
              selected={params.placement === placement}
              data-testid={TEST_IDS.framePlacement(placement)}
              onClick={() => setSelectedPhotoPlacement(placement)}
            />
          ))}
        </ChoiceGroup>
        {params.placement === PhotoPlacement.Free && freePlacement ? (
          <div className="space-y-1">
            <IconSlider
              min={FREE_PLACEMENT_MIN_SCALE}
              max={FREE_PLACEMENT_MAX_SCALE}
              step={0.01}
              value={freePlacement.scale}
              startIcon={<Minimize2 className="size-4" />}
              endIcon={<Maximize2 className="size-4" />}
              formatValue={(value) => `${value.toFixed(1)}×`}
              data-testid={TEST_IDS.framePrintSize}
              aria-label="Free placement print size"
              onValueChange={setSelectedFreePlacementScale}
              onValueCommit={(value) => setSelectedFreePlacementScale(value, true)}
            />
            <Button type="button" variant="ghost" size="sm" data-testid={TEST_IDS.frameRecenter} onClick={recenterSelectedFreePlacement}>Re-center</Button>
          </div>
        ) : null}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Shadows</h3>
        <ChoiceGroup mode="multiple" label="Frame shadows" className="grid-cols-2">
          <ChoiceTile
            ariaLabel="Inside shadow"
            label="Inside"
            illustration={<ShadowIllustration kind="inside" enabled={params.windowShadow} />}
            selected={params.windowShadow}
            controlRole="switch"
            data-testid={TEST_IDS.frameWindowShadow}
            onClick={() => updateSelectedFrameParams({ windowShadow: !params.windowShadow })}
          />
          <ChoiceTile
            ariaLabel="Behind print shadow"
            label="Behind"
            illustration={<ShadowIllustration kind="behind" enabled={params.printShadow} />}
            selected={params.printShadow}
            controlRole="switch"
            data-testid={TEST_IDS.framePrintShadow}
            onClick={() => updateSelectedFrameParams({ printShadow: !params.printShadow })}
          />
        </ChoiceGroup>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium">Details</h3>
        <ChoiceGroup mode="multiple" label="Frame details" className="grid-cols-3">
          <ChoiceTile
            ariaLabel="Written date"
            label="Written"
            illustration={<DateIllustration kind="written" enabled={params.showWrittenDate} />}
            selected={params.showWrittenDate}
            controlRole="switch"
            data-testid={TEST_IDS.frameWrittenDate}
            onClick={() => updateSelectedFrameParams({ showWrittenDate: !params.showWrittenDate })}
          />
          <ChoiceTile
            ariaLabel="Film stamp date"
            label="Stamp"
            illustration={<DateIllustration kind="stamp" enabled={params.showStampDate} />}
            selected={params.showStampDate}
            controlRole="switch"
            data-testid={TEST_IDS.frameStampDate}
            onClick={() => updateSelectedFrameParams({ showStampDate: !params.showStampDate })}
          />
          <ChoiceTile
            ariaLabel="Caption"
            label="Caption"
            illustration={<DateIllustration kind="caption" enabled={showCaptionInput} />}
            selected={showCaptionInput}
            onClick={() => {
              setCaptionOpen(true)
              requestAnimationFrame(() => captionInput.current?.focus())
            }}
          />
        </ChoiceGroup>
        {dateOpen ? (
          <input
            type="date"
            aria-label="Frame date"
            value={params.date ?? ''}
            data-testid={TEST_IDS.frameDate}
            onChange={(event) => updateSelectedFrameParams({ date: event.currentTarget.value ? toIsoDate(event.currentTarget.value) : null })}
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm shadow-sm"
          />
        ) : null}
        {showCaptionInput ? (
          <input
            ref={captionInput}
            type="text"
            maxLength={CAPTION_MAX_LENGTH}
            value={params.caption}
            data-testid={TEST_IDS.frameCaption}
            onChange={(event) => updateSelectedFrameParams({ caption: event.currentTarget.value }, false)}
            onBlur={() => commitHistory(EditAction.EditFrame)}
            className="h-9 w-full rounded-md border bg-transparent px-3 text-sm shadow-sm"
            placeholder="Caption"
            aria-label="Frame caption"
          />
        ) : null}
      </div>

      <MoreSection id={MorePanelId.Frame}>
        <ChoiceGroup mode="single" label="Date format" className="grid-cols-2" data-testid={TEST_IDS.frameDateFormat}>
          {Object.values(DateFormatId).map((dateFormatId) => (
            <ChoiceTile
              key={dateFormatId}
              ariaLabel={DATE_FORMAT_LABELS[dateFormatId]}
              illustration={<span className="text-xs tabular-nums">{DATE_FORMAT_LABELS[dateFormatId]}</span>}
              selected={params.dateFormatId === dateFormatId}
              onClick={() => updateSelectedFrameParams({ dateFormatId })}
            />
          ))}
        </ChoiceGroup>
      </MoreSection>
      <Button type="button" variant="ghost" data-testid={TEST_IDS.frameRemove} onClick={removeFrame}>Remove frame</Button>
    </section>
  )
}
